/**
 * View Mode Context
 * Phase 7: Auditor View / Internal View toggle
 *
 * Per Evans' feedback: "If an auditor's looking at that dashboard and seeing 95%,
 * their question's gonna be, what's the other 5%?"
 *
 * This context provides a global toggle between:
 * - Internal View: Shows all metrics, QMS Health, working notes, drafts
 * - Auditor View: Shows only approved, signed-off documents and evidence
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export type ViewMode = 'internal' | 'auditor'

interface ViewModeContextType {
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  isAuditorView: boolean
  isInternalView: boolean
  toggleViewMode: () => void
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined)

const VIEW_MODE_STORAGE_KEY = 'logidocs-view-mode'

export function ViewModeProvider({ children }: { children: ReactNode }) {
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    // Initialize from localStorage if available
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY)
      if (stored === 'auditor' || stored === 'internal') {
        return stored
      }
    }
    return 'internal' // Default to internal view
  })

  // Persist to localStorage when changed
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode)
  }, [viewMode])

  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode)
  }

  const toggleViewMode = () => {
    setViewModeState((current) => (current === 'internal' ? 'auditor' : 'internal'))
  }

  const value: ViewModeContextType = {
    viewMode,
    setViewMode,
    isAuditorView: viewMode === 'auditor',
    isInternalView: viewMode === 'internal',
    toggleViewMode,
  }

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  )
}

export function useViewMode() {
  const context = useContext(ViewModeContext)
  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider')
  }
  return context
}
